import React, { useState } from 'react';
import {
  MfaChallenge,
  MfaChallengeMode,
} from 'components/mfaChallenge/MfaChallenge';
import { PopupOrPanel } from 'components/popupsAndPanels/PopupAndPanels';
import { useToastMessage } from 'hooks/useToast';
import {
  MfaChallengeType,
  MfaGrantResult,
  describeMfaGrant,
  mfaTypeLabel,
  parseMfaChallengeTypes,
  supportedMfaChallengeTypes,
} from 'pages/mfa/models';
import { IProfileInfo } from './models';

type Props = {
  profileDetails: IProfileInfo;
  onChanged: () => void;
};

type ActiveChange = { mode: MfaChallengeMode; type: MfaChallengeType };

export const MfaSettings: React.FC<Props> = ({ profileDetails, onChanged }) => {
  const [active, setActive] = useState<ActiveChange | null>(null);
  const showToast = useToastMessage();

  const available = supportedMfaChallengeTypes(
    profileDetails.availableMfaChallengeTypes
  );
  const enabled = parseMfaChallengeTypes(
    profileDetails.enabledMfaChallengeTypes
  );

  if (available.length === 0) return null;

  const handleComplete = (result: MfaGrantResult | null) => {
    if (!active) return;
    const outcome = `${mfaTypeLabel(active.type)} verification ${
      active.mode === 'enable' ? 'enabled' : 'disabled'
    }.`;
    showToast(result ? `${outcome} ${describeMfaGrant(result)}` : outcome);
    setActive(null);
    onChanged();
  };

  return (
    <div className="ProfileSection MfaSettings">
      <h5 className="Name">Multi factor authentication</h5>

      <div className="MfaMethodList">
        {available.map((type) => {
          const isEnabled = enabled.includes(type);
          return (
            <button
              type="button"
              className={`MfaMethodRow ${isEnabled ? 'Selected' : ''}`}
              key={type}
              aria-pressed={isEnabled}
              title={
                isEnabled
                  ? `Turn off ${mfaTypeLabel(type).toLowerCase()}`
                  : `Set up ${mfaTypeLabel(type).toLowerCase()}`
              }
              onClick={() =>
                setActive({ mode: isEnabled ? 'disable' : 'enable', type })
              }
            >
              <span className="MfaMethodTick" aria-hidden="true" />
              <span className="MfaMethodLabel">{mfaTypeLabel(type)}</span>
            </button>
          );
        })}
      </div>

      {active && (
        <PopupOrPanel
          isPopup={true}
          isOpen={true}
          position="right"
          hdrTitle=""
          noHeader
          noContentContainer
          noFooter
          givenClass="MfaChallengeModal"
          handleClose={() => setActive(null)}
          width={460}
        >
          <MfaChallenge
            key={`${active.mode}-${active.type}`}
            mode={active.mode}
            layout="modal"
            challengeType={active.type}
            onComplete={handleComplete}
            onCancel={() => setActive(null)}
          />
        </PopupOrPanel>
      )}
    </div>
  );
};

export default MfaSettings;
